import React, { useEffect, useState } from 'react';
import './MapStylePanel.css';
import {
  clearStoredMapStyle,
  downloadMapStyleJson,
  isValidMapStyle,
  saveMapStyleFromMapToStorage,
  saveMapStyleObjectToStorage
} from '../../utils/mapStylePersistence';

const MapStylePanel = ({ mapRef, onClose }) => {
  const [styleJsonText, setStyleJsonText] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!mapRef?.current) return;
    const map = mapRef.current;

    const syncEditorFromMap = () => {
      try {
        const style = map.getStyle();
        if (isValidMapStyle(style)) {
          setStyleJsonText(JSON.stringify(style, null, 2));
          setMessage('');
        }
      } catch (error) {
        setMessage('Could not read current map style.');
      }
    };

    if (map.isStyleLoaded && map.isStyleLoaded()) {
      syncEditorFromMap();
    } else {
      map.once('style.load', syncEditorFromMap);
    }

    return () => {
      map.off('style.load', syncEditorFromMap);
    };
  }, [mapRef]);

  const loadCurrentJson = () => {
    if (!mapRef?.current) return;
    try {
      const style = mapRef.current.getStyle();
      if (!isValidMapStyle(style)) {
        setMessage('Current map style is invalid.');
        return;
      }
      setStyleJsonText(JSON.stringify(style, null, 2));
      setMessage('Loaded current style.');
    } catch (error) {
      setMessage('Failed to load current style.');
    }
  };

  const applyJsonStyle = () => {
    if (!mapRef?.current) return;
    let parsed;
    try {
      parsed = JSON.parse(styleJsonText);
    } catch (error) {
      setMessage('Invalid JSON syntax.');
      return;
    }

    if (!isValidMapStyle(parsed)) {
      setMessage('Invalid style schema. Expected version/sources/layers.');
      return;
    }

    try {
      mapRef.current.setStyle(parsed);
      saveMapStyleObjectToStorage(parsed);
      setMessage('Applied and saved style.');
    } catch (error) {
      setMessage('Failed to apply style JSON.');
    }
  };

  const exportJson = () => {
    if (!mapRef?.current) return;
    try {
      const style = mapRef.current.getStyle();
      if (!isValidMapStyle(style)) {
        setMessage('Cannot export invalid style.');
        return;
      }
      const ok = downloadMapStyleJson(style, 'tegola-style-export.json');
      setMessage(ok ? 'Exported style JSON.' : 'Export failed.');
    } catch (error) {
      setMessage('Export failed.');
    }
  };

  const saveForRefresh = () => {
    if (!mapRef?.current) return;
    const ok = saveMapStyleFromMapToStorage(mapRef.current);
    setMessage(ok ? 'Saved for refresh.' : 'Save failed.');
  };

  const clearSaved = () => {
    clearStoredMapStyle();
    setMessage('Cleared saved style. Refresh for default.');
  };

  return (
    <div className="map-style-overlay">
      <div className="map-style-panel">
        <div className="map-style-header">
          <h2>Tegola JSON Style</h2>
          <button className="map-style-close" onClick={onClose}>×</button>
        </div>
        <div className="map-style-body">
          <p>Edit/apply your map style JSON and export it.</p>
          <textarea
            className="map-style-textarea"
            value={styleJsonText}
            onChange={(e) => setStyleJsonText(e.target.value)}
            spellCheck={false}
            placeholder="Style JSON"
          />
          <div className="map-style-actions">
            <button type="button" className="btn-reset" onClick={loadCurrentJson}>Load Current JSON</button>
            <button type="button" className="btn-apply" onClick={applyJsonStyle}>Apply JSON</button>
            <button type="button" className="btn-reset" onClick={exportJson}>Export JSON</button>
            <button type="button" className="btn-reset" onClick={saveForRefresh}>Save for Refresh</button>
            <button type="button" className="btn-reset" onClick={clearSaved}>Clear Saved</button>
          </div>
          {message ? <p className="map-style-message">{message}</p> : null}
        </div>
      </div>
    </div>
  );
};

export default MapStylePanel;
